import React, { useState, useCallback } from 'react';
import Drawer from '../Common/Drawer/Drawer';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import InputText from '../Common/InputText';
import Toggle from '../Common/Toggle/Toggle';
import {
	FiltersDrawerProps,
	FiltersDrawerState,
	SelectedFilterItem,
	FormFieldChangeHandler,
	DropdownChangeHandler,
} from '../../apis/filtersDrawer/filtersDrawer.types';

const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
	onComplete,
	onCancel,
	searchParams,
	departments,
	baselineJobTitles,
	communities,
	phases,
	disciplines,
}) => {
	const [state, setState] = useState<FiltersDrawerState>({
		...searchParams,
	});

	// Generic form change handler
	const onFormChange: FormFieldChangeHandler = useCallback((name, value) => {
		setState((prev) => ({ ...prev, [name]: value }));
	}, []);

	// Text input change handler
	const onTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		onFormChange(e.target.name as keyof FiltersDrawerState, e.target.value);
	}, [onFormChange]);

	// Dropdown change handler (converts CSV to array of objects with ids)
	const onDropdownChange: DropdownChangeHandler = useCallback((csv, name) => {
		const arrayOfSelectedIds = csv.split(",");
		const objectsWithIds: SelectedFilterItem[] = [];

		// Only process if we have actual values (not empty string)
		if (arrayOfSelectedIds.length > 0 && arrayOfSelectedIds[0] !== "") {
			for (let i = 0; i < arrayOfSelectedIds.length; i++) {
				const id = arrayOfSelectedIds[i].trim();
				if (id) {
					objectsWithIds.push({ id });
				}
			}
		}

		onFormChange(name, objectsWithIds);
	}, [onFormChange]);

	// Apply filters handler
	const onApply = useCallback(() => {
		onComplete({ ...searchParams, ...state });
	}, [onComplete, state, searchParams]);

	// Toggle change handler
	const onToggleChange = useCallback((name: string, value: boolean) => {
		onFormChange(name as keyof FiltersDrawerState, value);
	}, [onFormChange]);

	// Extract IDs for dropdown values
	const departmentIds = state.departments.map((m) => m.id);
	const baselineJobTitleIds = state.baselineJobTitles.map((m) => m.id);
	const communityIds = state.communities.map((m) => m.id);
	const phaseIds = state.phases.map((m) => m.id);
	const disciplineIds = state.disciplines.map((m) => m.id);

	return (
		<Drawer
			isWide={true}
			title="Baseline Task List Filters"
			onSaveClick={onApply}
			saveText="Apply"
			onCancelClick={onCancel}
		>
			<div className="row">
				<div className="col-12">
					<InputText
						name="name"
						label="Name"
						type="text"
						placeholder="Name"
						value={state.name}
						onChange={onTextChange}
						isRequired={false}
					/>
				</div>
				<div className="col-12">
					<InputText
						name="additionalInfo"
						label="Additional Info"
						type="text"
						placeholder="Additional Info"
						value={state.additionalInfo}
						onChange={onTextChange}
						isRequired={false}
					/>
				</div>
				<div className="col-12">
					<Toggle
						name="isActive"
						text="Active"
						checked={state.isActive}
						onChange={onToggleChange}
					/>
				</div>
				<div className="col-12">
					<MultiSelectBox
						name="departments"
						label="Departments"
						idField="id"
						valueField="name"
						options={departments}
						selectedValues={departmentIds.toString()}
						onChange={onDropdownChange}
						isMulti={true}
					/>
				</div>
				<div className="col-12">
					<MultiSelectBox
						name="baselineJobTitles"
						label="Baseline Job Titles"
						idField="id"
						valueField="name"
						options={baselineJobTitles}
						selectedValues={baselineJobTitleIds.toString()}
						onChange={onDropdownChange}
						isMulti={true}
					/>
				</div>
				<div className="col-12">
					<MultiSelectBox
						name="communities"
						label="Communities"
						idField="id"
						valueField="name"
						options={communities}
						selectedValues={communityIds.toString()}
						onChange={onDropdownChange}
						isMulti={true}
					/>
				</div>
				<div className="col-12">
					<MultiSelectBox
						name="phases"
						label="Phases"
						idField="id"
						valueField="name"
						options={phases}
						selectedValues={phaseIds.toString()}
						onChange={onDropdownChange}
						isMulti={true}
					/>
				</div>
				<div className="col-12">
					<MultiSelectBox
						name="disciplines"
						label="Disciplines"
						idField="id"
						valueField="name"
						options={disciplines}
						selectedValues={disciplineIds.toString()}
						onChange={onDropdownChange}
						isMulti={true}
					/>
				</div>
			</div>
		</Drawer>
	);
};

export default FiltersDrawer;